import { z } from "zod";
import { ActionBundleSchema } from "./actions.js";
import { ExpectedEventStatusSchema, ExpectedEventTypeSchema } from "./expected-event.js";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
import { AgentExecutionModeSchema, RolloutPolicyReferenceSchema } from "./rollout-governance.js";
export const AtomicActionBindingSchema = z
    .object({
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    exactPayloadHash: Sha256Schema,
    exactPreviewHash: Sha256Schema,
    acknowledgedWarningIds: z.array(z.string().min(1).max(80)),
})
    .strict()
    .superRefine((binding, context) => {
    if (new Set(binding.acknowledgedWarningIds).size !== binding.acknowledgedWarningIds.length) {
        context.addIssue({
            code: "custom",
            path: ["acknowledgedWarningIds"],
            message: "Acknowledged warning identifiers must be unique",
        });
    }
});
export const AtomicAcceptanceRequestSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    bundleId: IdSchema,
    expectedBundleVersion: z.number().int().positive(),
    expectedBundleHash: Sha256Schema,
    actionBindings: z.array(AtomicActionBindingSchema).min(1).max(50),
    idempotencyKey: IdSchema,
})
    .strict()
    .superRefine((request, context) => {
    const actionIds = request.actionBindings.map((binding) => binding.actionId);
    if (new Set(actionIds).size !== actionIds.length) {
        context.addIssue({
            code: "custom",
            path: ["actionBindings"],
            message: "Atomic acceptance action identifiers must be unique",
        });
    }
});
/** A server-projected review snapshot that supplies the exact bindings required for AAL2 acceptance. */
const ActionBundleReviewShape = {
    bundle: ActionBundleSchema,
    actionBindings: z.array(AtomicActionBindingSchema).min(1).max(50),
    mode: AgentExecutionModeSchema,
    rolloutPolicy: RolloutPolicyReferenceSchema,
};
export const ActionBundleReviewSchema = z
    .object(ActionBundleReviewShape)
    .strict()
    .superRefine((review, context) => {
    const bundleActions = new Set(review.bundle.actions.map((action) => action.id));
    for (const [index, binding] of review.actionBindings.entries()) {
        if (!bundleActions.has(binding.actionId)) {
            context.addIssue({
                code: "custom",
                path: ["actionBindings", index, "actionId"],
                message: "Review binding must belong to the returned Care Bundle",
            });
        }
    }
});
export const ShadowEvaluationReviewSchema = z
    .object({
    ...ActionBundleReviewShape,
    mode: z.literal("shadow"),
    initiatedBy: IdSchema,
})
    .strict()
    .superRefine((review, context) => {
    const bundleActions = new Set(review.bundle.actions.map((action) => action.id));
    for (const [index, binding] of review.actionBindings.entries()) {
        if (!bundleActions.has(binding.actionId)) {
            context.addIssue({
                code: "custom",
                path: ["actionBindings", index, "actionId"],
                message: "Review binding must belong to the returned Care Bundle",
            });
        }
    }
});
export const FulfillmentAcceptanceSchema = z
    .object({
    fulfillmentId: IdSchema,
    actionId: IdSchema,
    actionVersion: z.coerce.number().int().positive(),
    provider: z.string().min(1),
    state: z.enum(["queued", "processing", "dispatched", "acknowledged", "failed", "cancelled"]),
})
    .strict();
export const ExpectedEventAcceptanceSchema = z
    .object({
    expectedEventId: IdSchema,
    actionId: IdSchema,
    actionVersion: z.coerce.number().int().positive(),
    eventType: ExpectedEventTypeSchema,
    status: ExpectedEventStatusSchema,
    expectedBy: IsoDateTimeSchema,
    overdueAt: IsoDateTimeSchema,
    ownerId: IdSchema,
})
    .strict();
export const LocalAcceptanceReceiptSchema = z
    .object({
    transactionId: IdSchema,
    approvalReceiptId: IdSchema,
    bundleId: IdSchema,
    bundleVersion: z.coerce.number().int().positive(),
    bundleContentHash: Sha256Schema,
    acceptedActionIds: z.array(IdSchema).min(1).max(50),
    acceptedBy: IdSchema,
    acceptedAt: IsoDateTimeSchema,
    bundleState: z.enum(["pending_approval", "queued"]),
    fulfillments: z.array(FulfillmentAcceptanceSchema).min(1),
    expectedEvents: z.array(ExpectedEventAcceptanceSchema).min(1),
})
    .strict()
    .superRefine((receipt, context) => {
    const acceptedActionIds = new Set(receipt.acceptedActionIds);
    if (acceptedActionIds.size !== receipt.acceptedActionIds.length) {
        context.addIssue({
            code: "custom",
            path: ["acceptedActionIds"],
            message: "Accepted action identifiers must be unique",
        });
    }
    const fulfillmentIds = new Set();
    const fulfilledActions = new Set();
    const actionVersions = new Map();
    for (const [index, fulfillment] of receipt.fulfillments.entries()) {
        if (!acceptedActionIds.has(fulfillment.actionId)) {
            context.addIssue({
                code: "custom",
                path: ["fulfillments", index, "actionId"],
                message: "Fulfillment must belong to an accepted action",
            });
        }
        if (fulfillmentIds.has(fulfillment.fulfillmentId)) {
            context.addIssue({
                code: "custom",
                path: ["fulfillments", index, "fulfillmentId"],
                message: "Fulfillment identifiers must be unique",
            });
        }
        if (fulfilledActions.has(fulfillment.actionId)) {
            context.addIssue({
                code: "custom",
                path: ["fulfillments", index, "actionId"],
                message: "Atomic acceptance must create exactly one fulfillment per action",
            });
        }
        fulfillmentIds.add(fulfillment.fulfillmentId);
        fulfilledActions.add(fulfillment.actionId);
        actionVersions.set(fulfillment.actionId, fulfillment.actionVersion);
    }
    for (const actionId of acceptedActionIds) {
        if (!fulfilledActions.has(actionId)) {
            context.addIssue({
                code: "custom",
                path: ["fulfillments"],
                message: `Accepted action ${actionId} is missing its fulfillment binding`,
            });
        }
    }
    const expectedEventIds = new Set();
    const eventActions = new Set();
    for (const [index, event] of receipt.expectedEvents.entries()) {
        if (!acceptedActionIds.has(event.actionId)) {
            context.addIssue({
                code: "custom",
                path: ["expectedEvents", index, "actionId"],
                message: "Expected Event must belong to an accepted action",
            });
        }
        if (expectedEventIds.has(event.expectedEventId)) {
            context.addIssue({
                code: "custom",
                path: ["expectedEvents", index, "expectedEventId"],
                message: "Expected Event identifiers must be unique",
            });
        }
        const expectedVersion = actionVersions.get(event.actionId);
        if (expectedVersion !== undefined && expectedVersion !== event.actionVersion) {
            context.addIssue({
                code: "custom",
                path: ["expectedEvents", index, "actionVersion"],
                message: "Expected Event action version must match its fulfillment",
            });
        }
        expectedEventIds.add(event.expectedEventId);
        eventActions.add(event.actionId);
    }
    for (const actionId of acceptedActionIds) {
        if (!eventActions.has(actionId)) {
            context.addIssue({
                code: "custom",
                path: ["expectedEvents"],
                message: `Accepted action ${actionId} is missing an owned Expected Event`,
            });
        }
    }
});
/** Identifier-only reference emitted once for each atomically accepted action. */
export const AcceptedClinicalActionReferenceSchema = z
    .object({
    transactionId: IdSchema,
    approvalReceiptId: IdSchema,
    bundleId: IdSchema,
    actionId: IdSchema,
    actionVersion: z.coerce.number().int().positive(),
    fulfillmentId: IdSchema,
    expectedEventId: IdSchema,
})
    .strict();
//# sourceMappingURL=atomic-acceptance.js.map